/**
 * The app's sound set: one sound per role. Files live in
 * `build-resources/assets/audio`, which the renderer imports through Vite and
 * the main process reads from `Resources/assets/audio` in a packaged build.
 *
 * Assets are peak-normalised to -6 dBFS, so the per-role balance lives here
 * and both playback paths apply it. Only the main process scales it further,
 * by the notification volume; interaction cues play at the role's own level.
 */

export const SOUND_IDS = [
  'listen-open',
  'listen-close',
  'discard',
  'done',
  'nothing',
  'problem',
  'attention',
  'ask',
  'sign-off',
  'hello',
] as const;

export type SoundId = (typeof SOUND_IDS)[number];

export const PLAY_NOTIFICATION_SOUND_CHANNEL = 'play-notification-sound';

export type PlayNotificationSoundRequest = { sound: SoundId; volume: number };

export const SOUND_FILE_NAMES: Record<SoundId, string> = {
  'listen-open': 'listen-open.wav',
  'listen-close': 'listen-close.wav',
  discard: 'discard.wav',
  done: 'done.wav',
  nothing: 'nothing.wav',
  problem: 'problem.wav',
  attention: 'attention.wav',
  ask: 'ask.wav',
  'sign-off': 'sign-off.mp3',
  hello: 'hello.mp3',
};

export const SOUND_GAINS: Record<SoundId, number> = {
  'listen-open': 0.21,
  'listen-close': 0.19,
  discard: 0.19,
  done: 0.5,
  nothing: 1,
  problem: 0.5,
  attention: 0.21,
  ask: 0.3,
  'sign-off': 0.5,
  hello: 0.9,
};

export const MAC_SYSTEM_SOUNDS = [
  'Basso',
  'Blow',
  'Bottle',
  'Frog',
  'Funk',
  'Glass',
  'Hero',
  'Morse',
  'Ping',
  'Pop',
  'Purr',
  'Sosumi',
  'Submarine',
  'Tink',
] as const;

export type MacSystemSound = (typeof MAC_SYSTEM_SOUNDS)[number];

export const DEFAULT_SOUND_CHOICE = 'default';

export type NotificationSoundChoice =
  | typeof DEFAULT_SOUND_CHOICE
  | MacSystemSound;

export function isNotificationSoundChoice(
  value: unknown
): value is NotificationSoundChoice {
  return (
    value === DEFAULT_SOUND_CHOICE ||
    MAC_SYSTEM_SOUNDS.includes(value as MacSystemSound)
  );
}

/** macOS ships these at uneven levels; measured to match `done` by short-term RMS */
export const MAC_SYSTEM_SOUND_GAINS: Record<MacSystemSound, number> = {
  Basso: 0.71,
  Blow: 0.34,
  Bottle: 0.6,
  Frog: 1.26,
  Funk: 0.43,
  Glass: 0.57,
  Hero: 0.27,
  Morse: 0.72,
  Ping: 0.76,
  Pop: 0.94,
  Purr: 1.07,
  Sosumi: 0.65,
  Submarine: 0.68,
  Tink: 0.58,
};
